// Studio engraving and UV printing on beauty products for PR mailers,
// seeding and influencer kits (not live). Targets (OpenSEO US, 2026-11-03,
// coordinator research): perfume bottle engraving / engraved perfume bottle /
// perfume engraving 1,900 each, custom compact mirror 1,600, engraved compact
// mirror 1,000, personalized makeup 880, lipstick engraving 880, custom
// hairbrush 1,300, personalized hair brush 880, bulk compact mirrors 590,
// personalized lipstick 320, engraved silver tray 260. Prices and examples:
// owner 2026-11-03, consistent with printaworld-feed agent/PRICING.md.
const { images } = require('../engraving-services')

const beautyProducts = {
  key: 'beautyProducts',
  path: '/services/laser-engraving/beauty-products/',
  reviewedDate: '2026-11-03',
  reviewedDateLabel: 'November 3, 2026',
  reviewedNote: 'Prices from PrintAWorld’s 2026 beauty and PR quotes',
  title: 'Perfume Bottle Engraving & Custom Compact Mirrors in Bulk',
  description:
    'Perfume bottle engraving, custom compact mirrors, personalized lipstick and hairbrushes for PR mailers and influencer kits. Setup $75–$150 + $10–$35/pc.',
  crumbs: [['Laser engraving', '/services/laser-engraving/']],
  crumb: 'Beauty products',
  eyebrow: 'Beauty products / PR mailers and influencer kits',
  heading:
    'Perfume bottle engraving, custom compact mirrors and personalized makeup for PR mailers.',
  answer:
    'PrintAWorld engraves and UV prints names and logos on beauty products in bulk at our New York studio: perfume bottles, lipstick caps, compact mirrors, palettes, hairbrushes and trays for PR mailers, seeding and influencer kits. Send the products plus 10–15% spares; we test on your actual SKU, then personalize the run and ship it back fast. Pricing is a $75–$150 setup plus $10–$35 per piece for laser engraving or $20–$30 per piece for UV printing; rush adds 50%.',
  cta: 'Get a beauty products quote',
  heroPhoto: {
    url: images.metal.url,
    alt: images.metal.alt,
    caption: 'Laser engraving on finished metal products',
  },
  keyFacts: [
    { label: 'Setup', value: '$75–$150', note: 'Per job, rush setup higher' },
    {
      label: 'Laser',
      value: '$10–$35 / pc',
      note: 'Metal, glass, coated caps',
    },
    { label: 'UV print', value: '$20–$30 / pc', note: 'Full color or white' },
    { label: 'Rush', value: '+50%', note: 'Overnight on some jobs' },
  ],
  sections: [
    {
      type: 'cards',
      id: 'items',
      eyebrow: 'What we personalize',
      title: 'Engraved perfume bottles, compacts, lipstick and brushes.',
      intro:
        'A name for each creator or a logo on every piece. You supply the products; we never swap them for blanks.',
      index: '01 / THE ITEMS',
      items: [
        {
          label: 'Fragrance',
          title: 'Engraved perfume bottles',
          body: 'Names, initials or a logo on glass bottles and metal or coated caps.',
          link: [
            'Bottle engraving',
            '/services/laser-engraving/bottle-engraving/',
          ],
        },
        {
          label: 'Compacts',
          title: 'Custom compact mirrors',
          body: 'Engraved compact mirrors and cushion compacts, one name each or a logo across a bulk run.',
        },
        {
          label: 'Lips',
          title: 'Personalized lipstick',
          body: 'Lipstick engraving on metal and coated caps for launch kits and creator gifts.',
        },
        {
          label: 'Face',
          title: 'Palettes',
          body: 'Names UV printed on plastic palette lids, in full color or white.',
        },
        {
          label: 'Hair',
          title: 'Custom hairbrushes',
          body: 'Personalized hairbrushes with a name or logo printed or engraved on the handle or back.',
        },
        {
          label: 'Events',
          title: 'Engraved trays and gifts',
          body: 'Engraved silver trays, mirrors and awards for beauty events and award nights.',
        },
      ],
    },
    {
      type: 'cards',
      id: 'laser-or-uv',
      eyebrow: 'Laser or UV',
      title: 'Which one your product needs.',
      index: '02 / THE METHOD',
      items: [
        {
          label: 'Laser engraving',
          title: 'Metal, glass and coated caps',
          body: 'A permanent, tone-on-tone mark. Engraving has no color; it removes coating or marks the surface. On glass, a marking compound (CerMark) gives a dark mark.',
        },
        {
          label: 'UV printing',
          title: 'Plastic palettes, compacts and brushes',
          body: 'Full-color or white ink printed onto the product, for names and logos in brand colors.',
          link: ['UV printing', '/uv-printing-services/'],
        },
        {
          label: 'Good to know',
          title: 'No metallic ink',
          body: 'We don’t print metallic silver or gold ink. The closest is a silver-gray. For a metal look, engraving a metal or coated cap usually works better.',
        },
      ],
    },
    {
      type: 'steps',
      id: 'process',
      eyebrow: 'How it works',
      title: 'From your SKU to a box of personalized products.',
      intro:
        'We test first, every time: finishes and coatings change how a product engraves or takes ink.',
      index: '03 / THE PROCESS',
      items: [
        {
          title: 'Send the list and artwork',
          body: 'Names (a spreadsheet works), your logo, the product and how many.',
        },
        {
          title: 'Send products plus spares',
          body: 'Drop off in Brooklyn or Glendale, or ship them, with 10–15% spares for testing.',
        },
        {
          title: 'Approve the test',
          body: 'We personalize a spare and send photos before the run.',
        },
        {
          title: 'We run and ship',
          body: 'Each piece is personalized, checked against the list, repacked and shipped or picked up.',
        },
      ],
    },
    {
      type: 'pricing',
      id: 'pricing',
      eyebrow: 'Pricing',
      title: 'Setup plus a price per piece.',
      index: '04 / THE PRICE',
      lead: {
        label: 'Setup',
        value: '$75–$150',
        note: 'Once per job; a rush setup costs more.',
      },
      rowsTitle: 'Per piece',
      rows: [
        { label: 'Laser engraving', value: '$10–$35 / pc' },
        { label: 'UV printing', value: '$20–$30 / pc' },
        { label: 'Rush', value: '+50%' },
        { label: 'Shipping and taxes', value: 'Not included' },
      ],
      notes: [
        'Examples from real jobs: names on 30 eyeshadow palettes, $150 setup + $30 a box; a logo on 100 mirror compacts, $75 + $15 each; names on 43 hairbrushes, $150 + $20 each.',
      ],
      cta: 'Price my run',
    },
    {
      type: 'cards',
      id: 'work',
      eyebrow: 'Real beauty jobs',
      title: 'Beauty personalization we’ve done.',
      intro: 'Described without the brand. Prices are what the job was quoted.',
      index: '05 / OUR WORK',
      items: [
        {
          title: '30 palettes in 2 days',
          body: 'Names UV printed on 30 eyeshadow palettes for a PR firm, in 2 days. $150 setup + $30 a box.',
        },
        {
          title: '100 mirror compacts',
          body: 'A logo on 100 mirror compacts for a PR mailer. $75 setup + $15 each.',
        },
        {
          title: '43 hairbrushes',
          body: 'A name on each of 43 hairbrushes for an experiential agency. $150 setup + $20 each.',
        },
        {
          title: 'A cushion compact overnight',
          body: 'A luxury beauty brand’s cushion compact, engraved overnight.',
        },
        {
          title: '105 silver trays',
          body: 'Individually engraved silver trays quoted for a beauty awards event: $350 setup + $40 each.',
        },
        {
          title: 'Lipstick-shaped trophies',
          body: 'For a beauty retailer’s creator-program event.',
          link: [
            'Product-shaped trophies',
            '/services/custom-trophy/product-shaped-trophies/',
          ],
        },
      ],
    },
    {
      type: 'contrast',
      id: 'pr',
      eyebrow: 'For PR firms and seeding teams',
      title: 'Your products, personalized and back in the mail fast.',
      paragraphs: [
        'Send the creator list and the products. We check every name against the list, keep spares aside for replacements, and repack for your mailer.',
        'Building the box too? We 3D print PR boxes shaped like your product, and run live engraving at launches.',
      ],
      cta: 'Plan a PR mailer',
    },
  ],
  reviews: 'laser-engraving',
  faqTitle: 'Beauty personalization questions, answered.',
  faqs: [
    {
      question: 'How much does perfume bottle engraving cost in bulk?',
      answer:
        'A one-time setup of $75–$150 per job, plus $10–$35 per piece for laser engraving. UV printing is $20–$30 per piece. Rush adds 50%. Taxes and shipping aren’t included.',
    },
    {
      question: 'Can you engrave custom compact mirrors in bulk?',
      answer:
        'Yes. We engraved a logo on 100 mirror compacts for a PR mailer at $75 setup plus $15 each. One name per compact works too; send the list as a spreadsheet.',
    },
    {
      question: 'Can you personalize lipstick?',
      answer:
        'Yes. Lipstick engraving works on metal and coated caps. We test your actual cap first and send photos before the run.',
    },
    {
      question: 'Laser engraving or UV printing: which is better for makeup?',
      answer:
        'Laser engraving for metal, glass and coated caps; UV printing for plastic palettes, compacts and brushes, or when you want color. We can’t print metallic ink; the closest is silver-gray.',
    },
    {
      question: 'How fast can you turn a PR mailer around?',
      answer:
        'Rush jobs can be fast: 30 palettes took 2 days and a cushion compact was engraved overnight. Rush adds 50%. Tell us your ship date when you send the request.',
    },
    {
      question: 'How many spares should we send?',
      answer:
        '10–15% extra. We use spares to test and to replace any piece that doesn’t pass inspection.',
    },
    {
      question: 'Do you supply the products?',
      answer:
        'No. You send your own products; we personalize them and send them back. For blanks, use your usual supplier.',
    },
    {
      question: 'Can you engrave at our event instead?',
      answer:
        'Yes. We run live engraving stations at beauty launches and pop-ups in New York.',
    },
  ],
  quote: {
    eyebrow: 'Start your run',
    title: 'Tell us about your mailer.',
    body: 'Send the product, the quantity, the names or logo and your ship date. We’ll reply with a test plan and a written quote.',
    checklist: [
      'The product (a link or photo) and how many',
      'Names list or logo file',
      'Where the mark goes and how big',
      'Your ship or in-hand date',
    ],
  },
  eventPlanner: false,
  related: [
    ['Live beauty engraving', '/services/laser-engraving/beauty-events/'],
    ['Bottle engraving', '/services/laser-engraving/bottle-engraving/'],
    ['3D-printed PR boxes', '/services/3d-printing/pr-boxes/'],
    ['Rush logo printing', '/services/uv-printing/rush-logo-printing/'],
  ],
  schema: {
    name: 'Engraving and UV printing on beauty products',
    alternateName: [
      'Perfume bottle engraving',
      'Engraved compact mirrors',
      'Personalized lipstick',
      'Custom hairbrushes',
      'PR mailer personalization',
    ],
    serviceType: 'Laser engraving and UV printing',
    areaServed: { '@type': 'Country', name: 'United States' },
    minPrice: 75,
  },
}

module.exports = { beautyProducts }
